import React, { ChangeEvent } from "react";
import { Grid, TextField } from "@mui/material";
import {
  convertNumberToWords,
  formatCurrency,
  parseAmount,
  roundCurrency,
} from "./common";
import {
  InternalCalculations,
  InternalInvoiceBase,
  InternalInvoiceBaseFields,
  createInternalBaseForm,
  generateInternalInvoicePdf,
  validateInternalBaseFields,
} from "./internalCommon";

export interface InternalIgstForm extends InternalInvoiceBase {
  igstRate: string;
}

export const createInternalIgstForm = (): InternalIgstForm => ({
  ...createInternalBaseForm(),
  igstRate: "18",
});

export const calculateInternalIgst = (
  form: InternalIgstForm
): InternalCalculations => {
  const taxableValue = parseAmount(form.taxableValue);
  const rate = Number.parseFloat(form.igstRate) || 0;

  if (!Number.isFinite(taxableValue)) {
    return {
      taxableValue: Number.NaN,
      taxOneLabel: "",
      taxOneRate: 0,
      taxOneAmount: Number.NaN,
      totalAmount: Number.NaN,
      amountInWords: "",
    };
  }

  const taxAmount = roundCurrency((taxableValue * rate) / 100);
  const total = roundCurrency(taxableValue + taxAmount);

  return {
    taxableValue,
    taxOneLabel: `IGST @${rate}%`,
    taxOneRate: rate,
    taxOneAmount: taxAmount,
    totalAmount: total,
    amountInWords: convertNumberToWords(total),
  };
};

export const validateInternalIgstForm = (form: InternalIgstForm) => {
  const validation = validateInternalBaseFields(form);
  const taxable = parseAmount(form.taxableValue);
  if (!Number.isFinite(taxable) || taxable <= 0) {
    validation.taxableValue = "Enter a valid amount";
  }
  const rate = Number.parseFloat(form.igstRate);
  if (!Number.isFinite(rate) || rate < 0) {
    validation.igstRate = "Enter a valid rate";
  }
  return validation;
};

export const generateInternalIgstInvoicePdf = (
  form: InternalIgstForm,
  calculations: InternalCalculations
) => {
  const head = [
    [
      "Description",
      "HSN/SAC",
      "Taxable Value",
      calculations.taxOneLabel,
      "Total",
    ],
  ];

  const body = [
    [
      form.description,
      form.hsnSac,
      formatCurrency(calculations.taxableValue),
      formatCurrency(calculations.taxOneAmount),
      formatCurrency(calculations.totalAmount),
    ],
    [
      "Total",
      "",
      formatCurrency(calculations.taxableValue),
      formatCurrency(calculations.taxOneAmount),
      formatCurrency(calculations.totalAmount),
    ],
  ];

  generateInternalInvoicePdf(form, calculations, head, body, "IGST");
};

interface InternalIgstFieldsProps {
  form: InternalIgstForm;
  errors: Record<string, string>;
  calculations: InternalCalculations;
  onChange: (
    field: keyof InternalIgstForm
  ) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onRegenerateUuid: () => void;
  onApplyDefaultInvoiceNumber: () => void;
}

export const InternalIgstFields: React.FC<InternalIgstFieldsProps> = ({
  form,
  errors,
  calculations,
  onChange,
  onRegenerateUuid,
  onApplyDefaultInvoiceNumber,
}) => {
  const onBaseChange = (field: keyof InternalInvoiceBase) =>
    onChange(field as keyof InternalIgstForm);

  return (
    <InternalInvoiceBaseFields
      form={form}
      errors={errors}
      onBaseChange={onBaseChange}
      onRegenerateUuid={onRegenerateUuid}
      onApplyInvoiceNumber={onApplyDefaultInvoiceNumber}
      calculations={calculations}
    >
      <Grid item xs={12} md={6}>
        <TextField
          label="IGST Percentage"
          value={form.igstRate}
          onChange={onChange("igstRate")}
          fullWidth
          error={Boolean(errors.igstRate)}
          helperText={errors.igstRate}
          type="number"
          inputProps={{ min: 0, step: "0.01" }}
        />
      </Grid>
    </InternalInvoiceBaseFields>
  );
};
